/**
 * DataTable Component
 * Reusable table with sorting, pagination, and empty states.
 * 
 * Usage:
 *   <DataTable
 *     data={employees}
 *     columns={[
 *       { key: 'name', label: 'Name', sortable: true },
 *       { key: 'email', label: 'Email' },
 *       { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
 *       { key: 'actions', label: '', render: (row) => <Button onClick={() => edit(row)}>Edit</Button> }
 *     ]}
 *     loading={loading}
 *     emptyMessage="No employees found"
 *     onRowClick={(row) => navigate(`/employees/${row.id}`)}
 *   />
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export function DataTable({
  data = [],
  columns = [],
  loading = false,
  emptyMessage = 'No data found',
  emptyIcon = null,
  onRowClick,
  sort,
  onSort,
  rowClassName,
  stickyHeader = false,
  testIdPrefix = 'table'
}) {
  // Loading skeleton
  if (loading) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col, i) => (
              <TableHead key={i}>{col.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i}>
              {columns.map((col, j) => (
                <TableCell key={j}>
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {emptyIcon && <div className="mb-4">{emptyIcon}</div>}
        <p>{emptyMessage}</p>
      </div>
    );
  }

  // Sort icon
  const SortIcon = (props) => {
    const { column } = props;
    if (!sort || sort.field !== column.key) {
      return <ChevronsUpDown className="ml-1 h-3 w-3 opacity-50" />;
    }
    return sort.order === 'asc' 
      ? <ChevronUp className="ml-1 h-3 w-3" />
      : <ChevronDown className="ml-1 h-3 w-3" />;
  };

  return (
    <Table>
      <TableHeader className={stickyHeader ? 'sticky top-0 bg-background z-10' : ''}>
        <TableRow>
          {columns.map((col, i) => (
            <TableHead
              key={i}
              className={`${col.sortable && onSort ? 'cursor-pointer select-none hover:bg-muted/50' : ''} ${col.className || ''}`}
              style={{ width: col.width }}
              onClick={() => col.sortable && onSort && onSort(col.key)}
            >
              <div className="flex items-center">
                {col.label}
                {col.sortable && onSort && <SortIcon column={col} />}
              </div>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((row, rowIndex) => (
          <TableRow
            key={row.id || rowIndex}
            className={`
              ${onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''}
              ${typeof rowClassName === 'function' ? rowClassName(row) : rowClassName || ''}
            `}
            onClick={() => onRowClick?.(row)}
            data-testid={`${testIdPrefix}-row-${row.id || rowIndex}`}
          >
            {columns.map((col, colIndex) => (
              <TableCell key={colIndex} className={col.cellClassName}>
                {col.render 
                  ? col.render(row, rowIndex)
                  : getNestedValue(row, col.key)
                }
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Helper to get nested values
function getNestedValue(obj, path) {
  if (!path) return null;
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}

export default DataTable;
